// Copyright (c) Jupyter Development Team.
// Copyright (c) Microsoft Corporation.
// Distributed under the terms of the Modified BSD License.
import { __decorate } from "tslib";
import { attr, css, observable } from '@microsoft/fast-element';
import { Select as FoundationSelect, selectTemplate as template } from '@microsoft/fast-foundation';
import { fillColor, heightNumberAsToken, neutralLayerFloating } from '../design-tokens.js';
import { selectStyles as styles } from './select.styles.js';
/**
 * Base class for Select.
 * @public
 */
export class Select extends FoundationSelect {
    constructor() {
        super(...arguments);
        /**
         * The cached scroll width of the listbox when visible.
         *
         * @internal
         */
        this.listboxScrollWidth = '';
    }
    /**
     * (Un-)set the width when the autoWidth property changes.
     *
     * @param prev - the previous autoWidth value
     * @param next - the current autoWidth value
     */
    autoWidthChanged(prev, next) {
        if (next) {
            this.setAutoWidth();
        }
        else {
            this.style.removeProperty('width');
        }
    }
    /**
     * Compute the listbox width to set the one of the input.
     */
    setAutoWidth() {
        if (!this.autoWidth || !this.isConnected) {
            return;
        }
        let listWidth = this.listbox.getBoundingClientRect().width;
        // If the list has not been displayed yet trick to get its size
        if (listWidth === 0 && this.listbox.hidden) {
            Object.assign(this.listbox.style, { visibility: 'hidden' });
            this.listbox.removeAttribute('hidden');
            listWidth = this.listbox.getBoundingClientRect().width;
            this.listbox.setAttribute('hidden', '');
            this.listbox.style.removeProperty('visibility');
        }
        if (listWidth > 0) {
            Object.assign(this.style, { width: `${listWidth}px` });
        }
    }
    /**
     * @internal
     */
    connectedCallback() {
        super.connectedCallback();
        this.setAutoWidth();
        if (this.listbox) {
            fillColor.setValueFor(this.listbox, neutralLayerFloating);
        }
    }
    /**
     * Synchronize the form-associated proxy and updates the value property of the element.
     *
     * @param prev - the previous collection of slotted option elements
     * @param next - the next collection of slotted option elements
     *
     * @internal
     */
    slottedOptionsChanged(prev, next) {
        super.slottedOptionsChanged(prev, next);
        this.setAutoWidth();
    }
    /**
     * Returns the calculated max height for the listbox.
     *
     * @internal
     * @remarks
     * Used to generate the `--listbox-max-height` CSS custom property.
     *
     */
    get listboxMaxHeight() {
        return Math.floor(this.maxHeight / heightNumberAsToken.getValueFor(this)).toString();
    }
    /**
     * @internal
     */
    listboxScrollWidthChanged() {
        this.updateComputedStylesheet();
    }
    /**
     * Returns the size value, if any. Otherwise, returns 4 if in
     * multi-selection mode, or 0 if in single-selection mode.
     *
     * @internal
     * @remarks
     * Used to generate the `--size` CSS custom property.
     *
     */
    get selectSize() {
        var _a;
        return `${(_a = this.size) !== null && _a !== void 0 ? _a : (this.multiple ? 4 : 0)}`;
    }
    /**
     * Updates the computed stylesheet when the multiple property changes.
     *
     * @param prev - the previous multiple value
     * @param next - the current multiple value
     *
     * @override
     * @internal
     */
    multipleChanged(prev, next) {
        super.multipleChanged(prev, next);
        this.updateComputedStylesheet();
    }
    /**
     * Sets the selectMaxSize design token when the maxHeight property changes.
     *
     * @param prev - the previous maxHeight value
     * @param next - the current maxHeight value
     *
     * @internal
     */
    maxHeightChanged(prev, next) {
        if (this.collapsible) {
            this.updateComputedStylesheet();
        }
    }
    setPositioning() {
        super.setPositioning();
        this.updateComputedStylesheet();
    }
    /**
     * Updates the component dimensions when the size property is changed.
     *
     * @param prev - the previous size value
     * @param next - the current size value
     *
     * @override
     * @internal
     */
    sizeChanged(prev, next) {
        super.sizeChanged(prev, next);
        this.updateComputedStylesheet();
        if (this.collapsible) {
            requestAnimationFrame(() => {
                this.listbox.style.setProperty('display', 'flex');
                this.listbox.style.setProperty('overflow', 'visible');
                this.listbox.style.setProperty('visibility', 'hidden');
                this.listbox.style.setProperty('width', 'auto');
                this.listbox.hidden = false;
                this.listboxScrollWidth = `${this.listbox.scrollWidth}`;
                this.listbox.hidden = true;
                this.listbox.style.removeProperty('display');
                this.listbox.style.removeProperty('overflow');
                this.listbox.style.removeProperty('visibility');
                this.listbox.style.removeProperty('width');
            });
            return;
        }
        this.listboxScrollWidth = '';
    }
    /**
     * Updates an internal stylesheet with calculated CSS custom properties.
     *
     * @internal
     */
    updateComputedStylesheet() {
        if (this.computedStylesheet) {
            this.$fastController.removeStyles(this.computedStylesheet);
        }
        this.computedStylesheet = css `
      :host {
        --listbox-max-height: ${this.listboxMaxHeight};
        --listbox-scroll-width: ${this.listboxScrollWidth};
        --size: ${this.selectSize};
      }
    `;
        this.$fastController.addStyles(this.computedStylesheet);
    }
}
__decorate([
    attr({ attribute: 'autowidth', mode: 'boolean' })
], Select.prototype, "autoWidth", void 0);
__decorate([
    attr({ attribute: 'minimal', mode: 'boolean' })
], Select.prototype, "minimal", void 0);
__decorate([
    observable
], Select.prototype, "listboxScrollWidth", void 0);
/**
 * A function that returns a {@link @microsoft/fast-foundation#Select} registration for configuring the component with a DesignSystem.
 * Implements {@link @microsoft/fast-foundation#selectTemplate}
 *
 *
 * @public
 * @remarks
 * Generates HTML Element: `<jp-select>`
 *
 */
export const jpSelect = Select.compose({
    baseName: 'select',
    baseClass: FoundationSelect,
    template,
    styles,
    indicator: /* html */ `
        <svg
            class="select-indicator"
            part="select-indicator"
            viewBox="0 0 12 7"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M11.85.65c.2.2.2.5 0 .7L6.4 6.84a.55.55 0 01-.78 0L.14 1.35a.5.5 0 11.71-.7L6 5.8 11.15.65c.2-.2.5-.2.7 0z"
            />
        </svg>
    `
});
export { styles as selectStyles };
